import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import OfertaStep1 from "../components/oferta/OfertaStep1";
import OfertaStep2 from "../components/oferta/OfertaStep2";
import { useAuth } from "../context/AuthContext";
import { useCredits } from "../context/CreditsContext";
import { FormProvider, useFormContext } from "../context/FormContext";
import sidebarLogo from "../assets/sidebar_logo.svg";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import {
  createOffer,
  updateOffer,
  getOfferById,
  sendOffer,
} from "../services/api";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import NotificationMessage from "../components/NotificationMessage";
import InsufficientCreditsModal from "../components/InsufficientCreditsModal.js";
import { cleanAmountValue, parseAmountToNumber, parseApiAmount, formatApiAmountForEdit } from '../utils/amountUtils';
import useNetworkStatus from "../hooks/useNetworkStatus";
import NetworkStatusModal from "../components/NetworkStatusModal";

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const SidebarLogo = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white; /* Fondo blanco como lo solicita el usuario */
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  color: #fd7e55;
  font-weight: 500;
  width: 100%;
  white-space: nowrap;
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  align-items: start;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 2rem;
  }
`;

const HeaderTitle = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Description = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  width: 100%;
  max-width: 48%;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
    display: none;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.8rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const ReadOnlyNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding: 1rem 0;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #dbe4f2;
  background-color: white;
  color: #a8b9d5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f5f8fd;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StepNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StepInfo = styled.div`
  color: #ff7057;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
`;



const NuevaOfertaPage = () => {
  const { user } = useAuth();
  const { credits, formatCreditsDisplay, decrementCredits } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlId } = useParams(); // Rename to urlId to avoid confusion
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [offerId, setOfferId] = useState(urlId); // Track offer ID separately
  const [pendingNewOfferId, setPendingNewOfferId] = useState(null);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [hasShownNetworkModal, setHasShownNetworkModal] = useState(false);

  // Hook para detectar el estado de la conexión
  const isOnline = useNetworkStatus();

  // Detectar si estamos en modo solo lectura basado en la ruta
  const isReadOnly = location.pathname.startsWith("/detalle-oferta/");

  // Resetear el estado cuando se recupera la conexión
  useEffect(() => {
    if (isOnline && hasShownNetworkModal) {
      setHasShownNetworkModal(false);
    }
  }, [isOnline, hasShownNetworkModal]);

  const [formData, setFormData] = useState({
    ofertante: {
      nombre: "",
      apellidos: "",
      email: "",
      identificacion: "",
      movil: "",
    },
    contraparte: {
      nombre: "",
      apellidos: "",
      email: "",
      identificacion: "",
      movil: "",
    },
    representacionContraparte: {
      activa: false,
      nombre: "",
      apellidos: "",
      email: "",
      identificacion: "",
      movil: "",
    },
    referencia: "",
    objeto: "",
    cuantia: "",
    confidencialidad: true,
    plazoRespuesta: "",
    contenido: "",
    archivo: null,
    aceptaCondiciones: false,
  });



  // Fetch existing offer data if editing
  useEffect(() => {
    const fetchOfferData = async () => {
      if (urlId) {
        try {
          console.log("Fetching offer data for ID:", urlId);
          const offerData = await getOfferById(urlId);
          console.log("API response data:", offerData);

          // Establecer el ID de la oferta
          setOfferId(urlId);

          // Verificar si las identificaciones parecen ser cifradas
          // (Los IDs cifrados suelen tener una longitud específica y estar en formato hexadecimal)
          const isIdEncrypted = (id) => {
            // Si el ID parece ser un hash (hexadecimal de longitud consistente 32+ caracteres)
            return id && typeof id === "string" && /^[a-f0-9]{32,}$/i.test(id);
          };

          // Log para verificar el estado de las identificaciones
          console.log("Estado de cifrado de las identificaciones:", {
            ofertanteID: {
              valor:
                offerData.offeror?.idValue ||
                offerData.representedparty_id ||
                "",
              cifrado: isIdEncrypted(
                offerData.offeror?.idValue || offerData.representedparty_id
              ),
            },
            contraparteID: {
              valor:
                offerData.counterparty?.idValue ||
                offerData.counterparty_id ||
                "",
              cifrado: isIdEncrypted(
                offerData.counterparty?.idValue || offerData.counterparty_id
              ),
            },
            representacionID: {
              valor: offerData.lawyer?.idValue || offerData.lawyer_id || "",
              cifrado: isIdEncrypted(
                offerData.lawyer?.idValue || offerData.lawyer_id
              ),
            },
          });

          // Map API response to form data structure
          const representacionActiva = !!offerData.lawyer?.firstName || !!offerData.lawyer_name || false;
          
          const mappedData = {
            ofertante: {
              nombre:
                offerData.offeror?.firstName ||
                offerData.representedparty_name ||
                "",
              apellidos:
                offerData.offeror?.lastNames ||
                offerData.representedparty_last_name ||
                "",
              email:
                offerData.offeror?.email ||
                offerData.representedparty_email ||
                "",
              identificacion:
                offerData.offeror?.idValue ||
                offerData.representedparty_id ||
                "",
              movil:
                offerData.offeror?.mobile ||
                offerData.representedparty_phone ||
                "",
            },
            contraparte: {
              nombre:
                offerData.counterparty?.firstName ||
                offerData.counterparty_name ||
                "",
              apellidos:
                offerData.counterparty?.lastNames ||
                offerData.counterparty_last_name ||
                "",
              email:
                offerData.counterparty?.email ||
                offerData.counterparty_email ||
                "",
              identificacion:
                offerData.counterparty?.idValue ||
                offerData.counterparty_id ||
                "",
              movil:
                offerData.counterparty?.mobile ||
                offerData.counterparty_phone ||
                "",
            },
            representacionContraparte: {
              activa: representacionActiva,
              nombre:
                offerData.lawyer?.firstName || offerData.lawyer_name || "",
              apellidos:
                offerData.lawyer?.lastNames || offerData.lawyer_last_name || "",
              email: offerData.lawyer?.email || offerData.lawyer_email || "",
              identificacion:
                offerData.lawyer?.idValue || offerData.lawyer_id || "",
              movil: offerData.lawyer?.mobile || offerData.lawyer_phone || "",
            },
            referencia: offerData.reference || offerData.offer_reference || "",
            objeto: offerData.subject || offerData.offer_subject || "",
            cuantia:
              offerData.amount !== undefined && offerData.amount !== null
                ? formatApiAmountForEdit(offerData.amount)
                : offerData.offer_total_amount !== undefined && offerData.offer_total_amount !== null
                ? formatApiAmountForEdit(offerData.offer_total_amount)
                : "",
            confidencialidad:
              offerData.confidential !== undefined
                ? offerData.confidential
                : offerData.offer_confidentiality || true,
            plazoRespuesta:
              offerData.deadlineToRespond ||
              (offerData.offer_response_deadline
                ? new Date(offerData.offer_response_deadline)
                    .toISOString()
                    .split("T")[0]
                : ""),
            contenido:
              offerData.writtenContent || offerData.offer_user_content || "",
            archivo: null, // Files are handled separately
            aceptaCondiciones: false, // This would need to be checked again by the user
          };

          // Log identificaciones para depurar el problema
          console.log("Identificaciones cargadas de la API:", {
            ofertanteID: mappedData.ofertante.identificacion,
            contraparteID: mappedData.contraparte.identificacion,
            representacionID:
              mappedData.representacionContraparte.identificacion,
          });

          setFormData(mappedData);

          // If offer has step 2 data completed, set the current step to 2 (only in edit mode, not in read-only mode)
          if (
            !isReadOnly &&
            (offerData.subject ||
              offerData.offer_subject ||
              offerData.amount ||
              offerData.offer_total_amount ||
              offerData.writtenContent ||
              offerData.offer_user_content)
          ) {
            setCurrentStep(2);
          }
        } catch (error) {
          console.error("Error fetching offer data:", error);
          setError(
            "No se pudo cargar la oferta. Por favor, inténtalo de nuevo."
          );
        }
      }
    };

    fetchOfferData();
  }, [urlId, isReadOnly]);

  const updateFormData = (section, data) => {
    console.log(`Updating section "${section}" with data:`, data);
    setFormData((prevData) => {
      const newData = { ...prevData, [section]: data };
      console.log("New form data:", newData);
      return newData;
    });
  };

  const nextStep = async (currentStepValues = null) => {
    // Verificar conexión después del primer intento
    if (!isOnline) {
      setShowNetworkModal(true);
      setHasShownNetworkModal(true);
      return;
    }

    try {
      // Si hay valores actualizados, intentar guardar primero
      if (currentStepValues) {
        console.log("Guardando borrador antes de avanzar al paso 2...");
        setIsSubmitting(true);

        try {
          // Verificar los datos recibidos de Step 1
          console.log("DATOS RECIBIDOS DE STEP 1:", {
            ofertanteNombre:
              currentStepValues.ofertante?.nombre || "NO PRESENTE",
            ofertanteIdentificacion:
              currentStepValues.ofertante?.identificacion || "NO PRESENTE",
            contraparteNombre:
              currentStepValues.contraparte?.nombre || "NO PRESENTE",
            contraparteIdentificacion:
              currentStepValues.contraparte?.identificacion || "NO PRESENTE",
          });

          // Crear una copia profunda de los valores actualizados para evitar problemas de referencia
          const valoresActualizados = {
            ...currentStepValues,
            ofertante: { ...currentStepValues.ofertante },
            contraparte: { ...currentStepValues.contraparte },
            representacionContraparte: {
              ...currentStepValues.representacionContraparte,
            },
          };

          // Usar los valores del paso 1 para guardar
          const { apiData, hasFiles } = mapFormDataToApiFormat(
            1,
            valoresActualizados
          );

          // Guardar/actualizar el borrador
          let response;
          if (offerId) {
            console.log("Actualizando oferta existente con ID:", offerId);
            response = await updateOffer(offerId, apiData, hasFiles);
          } else {
            console.log("Creando nueva oferta desde paso 1");
            response = await createOffer(apiData, hasFiles);
            // Guardar el ID de la oferta recibido
            if (response.id) {
              console.log("Nueva oferta creada con ID:", response.id);
              setOfferId(response.id);
              // Actualizar URL sin navegar
              window.history.replaceState(
                {},
                "",
                `/editar-oferta/${response.id}`
              );
            }
          }

          console.log(
            "Borrador guardado automáticamente antes de avanzar al paso 2:",
            response
          );

          // Si recibimos valores actualizados, actualizar formData
          setFormData((prevData) => ({
            ...prevData,
            ...valoresActualizados,
          }));
        } catch (error) {
          console.error("Error al guardar borrador antes de avanzar:", error);
          // Continuar al siguiente paso a pesar del error
        } finally {
          setIsSubmitting(false);
        }
      }

      // Avanzar al paso 2
      setCurrentStep(2);
    } catch (error) {
      console.error("Error al avanzar al paso 2:", error);
    }
  };

  const prevStep = () => {
    setCurrentStep(1);
  };

  // Funciones para navegación en modo solo lectura
  const handleReadOnlyBack = () => {
    navigate("/offers");
  };

  const handleReadOnlyNextStep = () => {
    setCurrentStep(2);
  };

  const handleReadOnlyPrevStep = () => {
    setCurrentStep(1);
  };



  // Map form data to API format
  const mapFormDataToApiFormat = (step, updatedValues = null) => {
    // Usar los valores actualizados o formData si no hay actualizaciones
    const currentData = updatedValues || formData;
    
    // Determinar si hay archivos considerando tanto archivos nuevos como existentes
    // Si el componente ya calculó hasFiles, usarlo; si no, calcularlo aquí
    let hasFiles;
    if (currentData.hasFiles !== undefined) {
      // Usar el valor hasFiles calculado por el componente
      hasFiles = currentData.hasFiles;
      console.log("Usando hasFiles del componente:", hasFiles);
    } else {
      // Fallback: calcular basándose en archivos nuevos y existentes
      hasFiles = !!(currentData.archivo || (currentData.existingFiles && currentData.existingFiles.length > 0));
      console.log("Calculando hasFiles localmente:", hasFiles);
    }

    console.log("DETERMINACIÓN FINAL DE ARCHIVOS:", {
      archivoNuevo: !!currentData.archivo,
      archivosExistentes: currentData.existingFiles?.length || 0,
      hasFilesComponente: currentData.hasFiles,
      hasFilesFinal: hasFiles,
      tipoEnvio: hasFiles ? "FormData" : "JSON"
    });

    // Verificar la estructura de datos antes de mapear
    console.log("VERIFICACIÓN ANTES DE MAPEO:", {
      tieneOfertante: !!currentData.ofertante,
      tieneContraparte: !!currentData.contraparte,
      objetoValor: currentData.objeto,
      identificacionOfertante:
        currentData.ofertante?.identificacion || "NO DISPONIBLE",
      identificacionContraparte:
        currentData.contraparte?.identificacion || "NO DISPONIBLE",
    });

    // Asegurarse de que los objetos anidados existan
    const ofertante = currentData.ofertante || {};
    const contraparte = currentData.contraparte || {};
    const representacion = currentData.representacionContraparte || {
      activa: false,
    };

    // Log antes de mapear para verificar presencia de identificaciones
    console.log("VALORES DE IDENTIFICACIÓN DISPONIBLES:", {
      ofertanteID: ofertante.identificacion,
      contraparteID: contraparte.identificacion,
      representacionID: representacion.activa
        ? representacion.identificacion
        : "N/A",
    });

    // Prepare the basic data structure
    const apiData = {
      step: step.toString(),
      encrypt_ids: true,
      offeror: {
        firstName: ofertante.nombre || "",
        lastNames: ofertante.apellidos || "",
        email: ofertante.email || "",
        idValue: ofertante.identificacion || "",
        mobile: ofertante.movil || "",
      },
      counterparty: {
        firstName: contraparte.nombre || "",
        lastNames: contraparte.apellidos || "",
        email: contraparte.email || "",
        idValue: contraparte.identificacion || "",
        mobile: contraparte.movil || "",
      },
      reference: currentData.referencia || "",
    };

    // Log the mapped data to verify it contains values
    console.log("Mapped API data:", apiData);
    console.log("VERIFICACIÓN DE IDENTIFICACIÓN MAPEADA:", {
      ofertanteID: apiData.offeror.idValue,
      contraparteID: apiData.counterparty.idValue,
    });

    // Add lawyer data - always send lawyer field, with empty values when toggle is disabled
    if (representacion.activa) {
      apiData.lawyer = {
        firstName: representacion.nombre || "",
        lastNames: representacion.apellidos || "",
        email: representacion.email || "",
        idValue: representacion.identificacion || "",
        mobile: representacion.movil || "",
      };
    } else {
      // When toggle is disabled, send empty lawyer data
      apiData.lawyer = {
        firstName: "",
        lastNames: "",
        email: "",
        idValue: "",
        mobile: "",
      };
    }

    // Add step 2 data if we're submitting the second step
    if (step === 2) {
      apiData.subject = currentData.objeto || "";

      // Usar la función unificada para limpiar el valor de cuantía
      let amount = 0;
      if (currentData.cuantia) {
        amount = parseAmountToNumber(currentData.cuantia.toString());
        
        if (isNaN(amount)) {
          console.warn("🔍 NuevaOfertaPage - Valor inválido, usando 0");
          amount = 0;
        }
      }
      apiData.amount = amount;

      console.log("🔍 CONFIDENCIALIDAD MAPEO API:", {
        confidencialidadOriginal: currentData.confidencialidad,
        tipoOriginal: typeof currentData.confidencialidad,
        valorConvertido: !!currentData.confidencialidad,
        timestamp: new Date().toISOString()
      });
      
      apiData.confidential = !!currentData.confidencialidad;
      apiData.deadlineToRespond = currentData.plazoRespuesta || "";
      apiData.writtenContent = currentData.contenido || "";

      // Log de datos para verificar
      console.log("DATOS STEP 2 A ENVIAR:", {
        subject: apiData.subject,
        amount: apiData.amount,
        confidential: apiData.confidential,
        deadlineToRespond: apiData.deadlineToRespond,
        writtenContent: apiData.writtenContent ? "presente" : "no presente",
      });

      // Add files if present - considerar tanto archivos nuevos como existentes
      if (hasFiles) {
        // Crear array de archivos
        const files = [];
        
        // Agregar archivo nuevo si existe
        if (currentData.archivo) {
          files.push(currentData.archivo);
        }
        
        // Nota: Los archivos existentes no se envían aquí porque ya están en el servidor
        // Solo necesitamos enviar archivos nuevos cuando hasFiles=true indica que hay cambios
        
        if (files.length > 0) {
          apiData.files = files;
        }
        
        console.log("ARCHIVOS A ENVIAR:", {
          archivoNuevo: !!currentData.archivo,
          totalArchivos: files.length,
          archivosExistentes: currentData.existingFiles?.length || 0
        });
      }
    }

    return { apiData, hasFiles };
  };

  const handleSaveDraft = async (currentStepValues = null) => {
    // Verificar conexión después del primer intento
    if (!isOnline) {
      setShowNetworkModal(true);
      setHasShownNetworkModal(true);
      return;
    }

    try {
      console.log("Current form data before mapping:", formData);

      setIsSavingDraft(true);
      setError(null);
      setSuccessMessage(null);
      setPendingNewOfferId(null);

      if (currentStepValues) {
        console.log(
          "Received updated values from step component:",
          currentStepValues
        );
        // Verificar identificaciones recibidas
        console.log("IDENTIFICACIONES RECIBIDAS:", {
          ofertanteID:
            currentStepValues.ofertante?.identificacion || "NO DISPONIBLE",
          contraparteID:
            currentStepValues.contraparte?.identificacion || "NO DISPONIBLE",
          representacionID: currentStepValues.representacionContraparte?.activa
            ? currentStepValues.representacionContraparte?.identificacion
            : "NO APLICABLE",
        });
      }

      try {
        // Crear una copia profunda de los valores actualizados para evitar problemas de referencia
        const valoresActualizados = currentStepValues
          ? {
              ...currentStepValues,
              ofertante: { ...currentStepValues.ofertante },
              contraparte: { ...currentStepValues.contraparte },
              representacionContraparte: {
                ...(currentStepValues.representacionContraparte || {
                  activa: false,
                }),
              },
            }
          : null;

        // Usar directamente los valores actualizados al mapear para API
        const { apiData, hasFiles } = mapFormDataToApiFormat(
          currentStep,
          valoresActualizados
        );

        console.log("🔍 NuevaOfertaPage - API data after mapping:", apiData);


        let response;
        if (offerId) {
          // Update existing offer
          console.log("🔍 NuevaOfertaPage - Actualizando oferta existente con ID:", offerId);
          console.log("🔍 NuevaOfertaPage - Datos a enviar:", JSON.stringify(apiData, null, 2));
          response = await updateOffer(offerId, apiData, hasFiles);
        } else {
          // Create new offer
          console.log("🔍 NuevaOfertaPage - Creando nueva oferta desde paso", currentStep);
          console.log("🔍 NuevaOfertaPage - Datos a enviar:", JSON.stringify(apiData, null, 2));
          response = await createOffer(apiData, hasFiles);
          // Guardar el ID de la oferta recibido
          if (response.id) {
            console.log("Nueva oferta creada con ID:", response.id);
            setOfferId(response.id);
            // Mostrar mensaje de éxito y guardar el id pendiente para navegar después
            setSuccessMessage("Tu borrador se ha guardado correctamente");
            setPendingNewOfferId(response.id);
            return;
          }
        }

        // Si recibimos valores actualizados, actualizar formData después de guardar
        if (valoresActualizados) {
          setFormData((prevData) => ({
            ...prevData,
            ...valoresActualizados,
          }));
        }

        // If this is a new offer and we got an ID back, update the URL
        if (!offerId && response.id) {
          setOfferId(response.id);
          navigate(`/editar-oferta/${response.id}`);
        }
        // Mostrar mensaje de éxito
        setSuccessMessage("Tu borrador se ha guardado correctamente");
      } catch (mappingError) {
        console.error("Error en el mapeo de datos o guardado:", mappingError);
        setError(
          "Error en la preparación de datos. Por favor, verifica que todos los campos estén correctos."
        );
        throw mappingError;
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      setError("No se pudo guardar la oferta. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (currentStepValues = null) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (currentStepValues) {
        console.log(
          "Received updated values for submission:",
          currentStepValues
        );
        // Verificar identificaciones recibidas
        console.log("IDENTIFICACIONES RECIBIDAS PARA ENVÍO:", {
          ofertanteID:
            currentStepValues.ofertante?.identificacion || "NO DISPONIBLE",
          contraparteID:
            currentStepValues.contraparte?.identificacion || "NO DISPONIBLE",
          representacionID: currentStepValues.representacionContraparte?.activa
            ? currentStepValues.representacionContraparte?.identificacion
            : "NO APLICABLE",
        });
      }

      try {
        // Crear una copia profunda de los valores actualizados para evitar problemas de referencia
        const valoresActualizados = currentStepValues
          ? {
              ...currentStepValues,
              ofertante: { ...currentStepValues.ofertante },
              contraparte: { ...currentStepValues.contraparte },
              representacionContraparte: {
                ...(currentStepValues.representacionContraparte || {
                  activa: false,
                }),
              },
            }
          : null;

        // For final submission, we always send step 2 data
        // Usar directamente los valores actualizados
        const { apiData, hasFiles } = mapFormDataToApiFormat(
          2,
          valoresActualizados
        );

        // First, save/update the draft
        let response;
        let finalOfferId = offerId;

        if (offerId) {
          // Update existing offer
          console.log("Actualizando oferta existente con ID:", offerId);
          response = await updateOffer(offerId, apiData, hasFiles);
        } else {
          // Create new offer
          console.log("Creando nueva oferta antes de enviar");
          response = await createOffer(apiData, hasFiles);
          finalOfferId = response.id;
          if (finalOfferId) {
            setOfferId(finalOfferId);
          }
        }

        if (!finalOfferId) {
          throw new Error("No se pudo obtener el ID de la oferta");
        }

        // Si recibimos valores actualizados, actualizar formData después de guardar
        if (valoresActualizados) {
          setFormData((prevData) => ({
            ...prevData,
            ...valoresActualizados,
          }));
        }

        try {
          // Now send the offer through Evidence service
          console.log("Enviando oferta con ID:", finalOfferId);
          const sendResponse = await sendOffer(finalOfferId);
          
          // DECREMENTAR CRÉDITOS AUTOMÁTICAMENTE
          decrementCredits(1);
          
          // Redirigir a la pestaña "Enviados" del listado
          navigate("/offers", { 
            state: { 
              activeTab: 'enviados',
              refreshList: true 
            } 
          });
        } catch (sendError) {
          // Handle specific error cases for sending
          if (sendError.response) {
            // Check for insufficient credits error first
            if (sendError.response.data?.error === "insufficient_credits") {
              setInsufficientCreditsData({
                creditsAvailable: sendError.response.data.credits_available || 0,
                creditsRequired: sendError.response.data.credits_required || 1
              });
              setShowInsufficientCreditsModal(true);
              throw sendError; // Re-lanzar para que el catch exterior lo capture
            }
            
            switch (sendError.response.status) {
              case 401:
                setError(
                  "No estás autorizado para enviar esta oferta. Por favor, inicia sesión nuevamente."
                );
                break;
              case 403:
                setError(
                  "No se puede enviar la oferta: ya fue enviada o tiene datos incompletos. Revisa todos los campos requeridos."
                );
                break;
              case 404:
                setError("La oferta no existe o no tienes acceso a ella.");
                break;
              case 500:
                setError(
                  "Error del servidor al enviar la oferta. Por favor, inténtalo de nuevo más tarde."
                );
                break;
              default:
                setError(
                  "Error al enviar la oferta: " +
                    (sendError.message || "Error desconocido")
                );
            }
          } else {
            setError(
              "No se pudo enviar la oferta. Por favor, inténtalo de nuevo."
            );
          }
          throw sendError; // Re-lanzar para que el catch exterior lo capture
        }
      } catch (mappingError) {
        console.error("Error en el mapeo de datos o guardado:", mappingError);
        setError(
          "Error en la preparación de datos. Por favor, verifica que todos los campos estén correctos."
        );
        throw mappingError;
      }
    } catch (error) {
      console.error("Error submitting offer:", error);
      if (!error.message.includes("Error en la preparación de datos")) {
        setError(
          "No se pudo guardar o enviar la oferta. Por favor, inténtalo de nuevo."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogo src={sidebarLogo} alt="Logo" />
      </Sidebar>
      <ContentArea>
        <FormHeader>
          <HeaderContent>
            <HeaderTitle>
              {isReadOnly ? "Detalle de Oferta" : "Nueva Oferta Vinculante"}
            </HeaderTitle>
            {currentStep === 1 && !isReadOnly && (
              <Description>
                Crea una nueva oferta en <b>dos sencillos pasos</b>, nos
                encargaremos de enviarla de forma segura y confidencial. Antes
                de enviarla podrás visualizar una vista preliminar del documento
                y tendrás información en todo momento de su estado
              </Description>
            )}
            {isReadOnly && (
              <Description>
                Información detallada de la oferta en modo solo lectura
              </Description>
            )}
          </HeaderContent>
          <CreditsCounter>
            <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
            <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
          </CreditsCounter>
        </FormHeader>

        {/* Mostrar error como modal popup */}
        {error && (
          <NotificationMessage
            title="Error"
            message={error}
            isError={true}
            isModal={true}
            onClose={() => setError(null)}
          />
        )}
        {/* Mostrar éxito como modal popup */}
        {successMessage && (
          <NotificationMessage
            title="Éxito"
            message={successMessage}
            isError={false}
            isModal={true}
            onClose={() => {
              setSuccessMessage(null);
              if (pendingNewOfferId) {
                navigate(`/editar-oferta/${pendingNewOfferId}`);
                setPendingNewOfferId(null);
              }
            }}
          />
        )}

        {/* Modal de créditos insuficientes */}
        <InsufficientCreditsModal
          isOpen={showInsufficientCreditsModal}
          onClose={() => {
            setShowInsufficientCreditsModal(false);
            setInsufficientCreditsData(null);
          }}
          creditsAvailable={insufficientCreditsData?.creditsAvailable}
          creditsRequired={insufficientCreditsData?.creditsRequired}
        />

        {/* Modal de estado de conexión */}
        <NetworkStatusModal
          isOpen={showNetworkModal}
          onClose={() => setShowNetworkModal(false)}
        />

        {currentStep === 1 ? (
          <OfertaStep1
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            saveDraft={handleSaveDraft}
            isSubmitting={isSubmitting}
            isSavingDraft={isSavingDraft}
            isReadOnly={isReadOnly}
            hasShownNetworkModal={hasShownNetworkModal}
            isOnline={isOnline}
          />
        ) : (
          <OfertaStep2
            formData={formData}
            updateFormData={updateFormData}
            prevStep={prevStep}
            saveDraft={handleSaveDraft}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isSavingDraft={isSavingDraft}
            isReadOnly={isReadOnly}
            hasShownNetworkModal={hasShownNetworkModal}
            isOnline={isOnline}
          />
        )}

        {/* Navegación para modo solo lectura */}
        {isReadOnly && (
          <ReadOnlyNavigation>
            <NavButton onClick={handleReadOnlyBack}>
              ← Volver a Mis expedientes
            </NavButton>

            <StepNavigation>
              {currentStep === 2 && (
                <NavButton onClick={handleReadOnlyPrevStep}>
                  ← Anterior
                </NavButton>
              )}

              <StepInfo>Paso {currentStep} de 2</StepInfo>

              {currentStep === 1 && (
                <NavButton onClick={handleReadOnlyNextStep}>
                  Siguiente →
                </NavButton>
              )}
            </StepNavigation>
          </ReadOnlyNavigation>
        )}

        <Footer />
      </ContentArea>
    </PageContainer>
  );
};

export default NuevaOfertaPage;
